import { SB } from './storybook-ui'
import type { NavTree, StorybookNavNode } from './convert-to-storybook-nav-tree'
import {
  startTransition,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
} from '../components/combobox'
import type { StorybookSettings as StorybookSettingsType } from './storybook-settings'
import { type Toast, Toaster } from './toaster'
import { useTheme } from './use-theme'
import { useEventListener } from '@w/utils-fe'
import type { Equal, Expect } from '@w/utils'
import { formatLocalVersion } from '@w/utils'
import englishFlag from 'circle-flags/flags/us.svg'
import russianFlag from 'circle-flags/flags/ru.svg'
import { addStorybookRule } from './storybook-rules'
import { LoadingIndicator } from '../components/loading-indicator'

declare const APP_VERSION: unknown

interface Base {
  onStoryClick: (id: string) => void
  onNodeClick: (id: string) => void
  activeId?: string
  pendingId?: string
  /** Search mode: expand every rendered node regardless of activeId. */
  forceOpen?: boolean
  /** Search mode: id of the arrow-key-highlighted leaf. */
  highlightedId?: string
  /** Search mode: reports which leaf the pointer is hovering, to sync arrow-key position. */
  onHighlight?: (id: string) => void
}

interface StorybookSidebarProps extends Base {
  navTree: NavTree
  settings: {
    setFakeNetwork: (
      cb: (
        old: StorybookSettingsType['fakeNetwork'],
      ) => StorybookSettingsType['fakeNetwork'],
    ) => void
    setLanguage: (language: StorybookSettingsType['language']) => void
    setMinimal: (minimal: StorybookSettingsType['minimal']) => void
    setSourceColor: (sourceColor: StorybookSettingsType['sourceColor']) => void
    setRules: (
      cb: (
        old: StorybookSettingsType['rules'],
      ) => StorybookSettingsType['rules'],
    ) => void
    toggleTheme: () => void
    rerenderStory: () => void
  } & StorybookSettingsType
  toasts: Toast[]
  onToastDismiss: (id: string) => void
  rightSidebar?: boolean
  defaultControlledEndpoint?: string
  openReactQueryDevtools?: () => void
  openRouterDevtools?: () => void
  openTelemetryDevtools?: () => void
}

export function StorybookSidebar({
  navTree,
  settings,
  toasts,
  onToastDismiss,
  rightSidebar,
  defaultControlledEndpoint,
  openTelemetryDevtools,
  ...props
}: StorybookSidebarProps) {
  const explorers: Explorer[] = [
    {
      name: 'Stories Explorer',
      logo: SB.icons.BookMarked,
      version: formatLocalVersion(APP_VERSION),
    },
    {
      name: 'TRPC Explorer',
      logo: SB.icons.ServerCog,
      version: formatLocalVersion(APP_VERSION),
    },
  ]
  const [activeExplorerName, setActiveExplorerName] = useState(
    explorers[0].name,
  )
  const [searchOpen, setSearchOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus()
  }, [searchOpen])

  useEventListener('keydown', (e) => {
    if (e.altKey && e.code === 'KeyK') {
      e.preventDefault()
      setSearchOpen(true)
    }
  })

  return (
    <SB.Sidebar.Root
      side={rightSidebar ? 'right' : 'left'}
      variant={rightSidebar ? 'floating' : 'sidebar'}
    >
      <SB.Sidebar.Header>
        <ExplorerSwitcher
          activeExplorerName={activeExplorerName}
          setActiveExplorerName={setActiveExplorerName}
          explorers={explorers}
          settings={settings}
          defaultControlledEndpoint={defaultControlledEndpoint}
          openReactQueryDevtools={props.openReactQueryDevtools}
          openRouterDevtools={props.openRouterDevtools}
          openTelemetryDevtools={openTelemetryDevtools}
        />
        <StorybookSettings
          settings={settings}
          onSearchToggle={() => {
            setSearchOpen((open) => !open)
          }}
        />
      </SB.Sidebar.Header>
      <SidebarSearch
        navTree={navTree}
        active={searchOpen}
        pendingId={props.pendingId}
        inputRef={searchInputRef}
        onNavigate={props.onStoryClick}
        onNodeClick={props.onNodeClick}
        onClose={() => {
          setSearchOpen(false)
        }}
      >
        <SB.Sidebar.Content className="-mx-4 px-4">
          {navTree.navigation.map((item) => (
            <NavItem key={item.id} item={item} {...props} />
          ))}
        </SB.Sidebar.Content>
      </SidebarSearch>
      <Toaster toasts={toasts} onDismiss={onToastDismiss} />
      <SB.Sidebar.Rail />
    </SB.Sidebar.Root>
  )
}

function StorybookSettings({
  settings,
  onSearchToggle,
}: {
  settings: StorybookSidebarProps['settings']
  onSearchToggle: () => void
}) {
  const networkOptions = [
    { value: 'off', icon: SB.icons.WifiOff, label: 'Off' },
    { value: 'fast', icon: SB.icons.Rabbit, label: 'Fast' },
    { value: 'slow', icon: SB.icons.Turtle, label: 'Slow' },
    { value: 'fail', icon: SB.icons.CircleX, label: 'Fail' },
  ] as const
  type NetworkOPtionsValue = (typeof networkOptions)[number]['value']
  type _guard1 = Expect<
    Equal<NetworkOPtionsValue, StorybookSettingsType['fakeNetwork']['type']>
  >

  const languageOptions = [
    { value: 'en', flag: englishFlag, label: 'English' },
    { value: 'ru', flag: russianFlag, label: 'Russian' },
  ] as const
  type LanguageOptionsValue = (typeof languageOptions)[number]['value']
  type _guard2 = Expect<
    Equal<LanguageOptionsValue, StorybookSettingsType['language']>
  >

  const theme = useTheme(settings.reversedTheme)

  const getNetworkOption = () => {
    return (
      networkOptions.find(
        (option) => option.value === settings.fakeNetwork.type,
      ) ?? networkOptions[0]
    )
  }

  const getLanguageFlag = () => {
    return (
      languageOptions.find((option) => option.value === settings.language)
        ?.flag ?? englishFlag
    )
  }

  const networkOption = getNetworkOption()
  const NetworkIcon = networkOption.icon

  return (
    <SB.Sidebar.Group className="py-0">
      <SB.Sidebar.GroupContent>
        <div className="flex flex-col gap-4">
          <div className="flex gap-1">
            <SB.DropdownMenu.Root>
              <SB.DropdownMenu.Trigger
                render={(props) => (
                  <SB.IconButton
                    {...props}
                    className="bg-surface-3"
                    variant="outlined"
                    semanticRole="primary"
                  >
                    <LanguageFlag source={getLanguageFlag()} />
                    App language ⌥L
                  </SB.IconButton>
                )}
              />
              <SB.DropdownMenu.Content className="w-[160px] p-2">
                <SB.DropdownMenu.RadioGroup
                  value={settings.language}
                  onValueChange={(v) => {
                    settings.setLanguage(v as 'en' | 'ru')
                  }}
                >
                  {languageOptions.map((language) => (
                    <SB.DropdownMenu.RadioItem
                      key={language.value}
                      value={language.value}
                    >
                      <LanguageFlag source={language.flag} />
                      {language.label}
                    </SB.DropdownMenu.RadioItem>
                  ))}
                </SB.DropdownMenu.RadioGroup>
              </SB.DropdownMenu.Content>
            </SB.DropdownMenu.Root>
            <PaletteSettings settings={settings} />
            <SB.IconButton
              className="bg-surface-3"
              variant="outlined"
              semanticRole="primary"
              onClick={settings.toggleTheme}
            >
              {theme === 'dark' ? <SB.icons.Sun /> : <SB.icons.Moon />}
              {theme === 'dark' ? 'Light ⌥T' : 'Dark ⌥T'}
            </SB.IconButton>
            <SB.DropdownMenu.Root>
              <SB.DropdownMenu.Trigger
                render={(props) => (
                  <SB.IconButton
                    {...props}
                    className="bg-surface-3"
                    variant="outlined"
                    semanticRole="primary"
                  >
                    <NetworkIcon />
                    {`Network: ${networkOption.label}`}
                  </SB.IconButton>
                )}
              />
              <SB.DropdownMenu.Content>
                <SB.DropdownMenu.RadioGroup
                  value={settings.fakeNetwork.type}
                  onValueChange={(v) => {
                    settings.setFakeNetwork((old) => ({
                      ...old,
                      type: v as NetworkOPtionsValue,
                    }))
                  }}
                >
                  {networkOptions.map((option) => {
                    const OptionIcon = option.icon
                    return (
                      <SB.DropdownMenu.RadioItem
                        key={option.value}
                        value={option.value}
                      >
                        <OptionIcon />
                        {option.label}
                      </SB.DropdownMenu.RadioItem>
                    )
                  })}
                </SB.DropdownMenu.RadioGroup>
              </SB.DropdownMenu.Content>
            </SB.DropdownMenu.Root>
            <SB.IconButton
              className="bg-surface-3 ml-auto"
              variant="outlined"
              semanticRole="primary"
              onClick={onSearchToggle}
            >
              <SB.icons.SearchCode />
              Search ⌥K
            </SB.IconButton>
            <SB.IconButton
              className="bg-surface-3"
              variant="outlined"
              semanticRole="primary"
              onClick={settings.rerenderStory}
            >
              <SB.icons.RotateCw />
              Rerender story ⌥R
            </SB.IconButton>
          </div>
          {settings.fakeNetwork.preciseControl && (
            <TokenInput
              setTokens={(cb) => {
                settings.setFakeNetwork((old) => ({
                  ...old,
                  controlledEndpoints: cb(old.controlledEndpoints),
                }))
              }}
              tokens={settings.fakeNetwork.controlledEndpoints}
            />
          )}
        </div>
      </SB.Sidebar.GroupContent>
    </SB.Sidebar.Group>
  )
}

function PaletteSettings({
  settings,
}: {
  settings: StorybookSidebarProps['settings']
}) {
  const [sourceColor, setSourceColor] = useState({
    input: settings.sourceColor,
    setting: settings.sourceColor,
  })

  if (sourceColor.setting !== settings.sourceColor) {
    setSourceColor({
      input:
        sourceColor.input === sourceColor.setting
          ? settings.sourceColor
          : sourceColor.input,
      setting: settings.sourceColor,
    })
  }

  const deferredSourceColor = useDeferredValue(sourceColor.input)

  useEffect(() => {
    if (
      deferredSourceColor !== sourceColor.input ||
      deferredSourceColor === settings.sourceColor
    )
      return

    startTransition(() => {
      settings.setSourceColor(deferredSourceColor)
    })
  }, [deferredSourceColor, settings, sourceColor.input])

  return (
    <SB.DropdownMenu.Root>
      <SB.DropdownMenu.Trigger
        render={(props) => (
          <SB.IconButton
            {...props}
            className="bg-surface-3"
            variant="outlined"
            semanticRole="primary"
          >
            <PaletteSwatch
              minimal={settings.minimal}
              sourceColor={sourceColor.input}
            />
            {`Palette: ${settings.minimal ? 'Minimal' : 'Chromatic'}`}
          </SB.IconButton>
        )}
      />
      <SB.DropdownMenu.Content align="start" className="w-52 p-3">
        <div className="flex flex-col gap-4">
          <label
            className="flex cursor-pointer items-center justify-between gap-4"
            htmlFor="storybook-palette-source-color"
          >
            <SB.Text size="label">Source color</SB.Text>
            <input
              aria-label="Source color"
              className="size-8 cursor-pointer rounded-full border-0 bg-transparent p-0 [&::-moz-color-swatch]:rounded-full [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0"
              data-testid="storybook-palette-source-color"
              id="storybook-palette-source-color"
              onChange={(event) => {
                setSourceColor((current) => ({
                  ...current,
                  input: event.target.value,
                }))
              }}
              type="color"
              value={sourceColor.input}
            />
          </label>
          <label
            className="flex cursor-pointer items-center justify-between gap-4"
            htmlFor="storybook-palette-minimal"
          >
            <SB.Text size="label">Minimal</SB.Text>
            <SB.Switch
              checked={settings.minimal}
              data-testid="storybook-palette-minimal"
              id="storybook-palette-minimal"
              onCheckedChange={settings.setMinimal}
            />
          </label>
        </div>
      </SB.DropdownMenu.Content>
    </SB.DropdownMenu.Root>
  )
}

function LanguageFlag({ source }: { source: string }) {
  return (
    <img className="size-5 shrink-0" src={source} alt="" aria-hidden="true" />
  )
}
LanguageFlag.displayName = 'LanguageFlagIcon'

function PaletteSwatch({
  minimal,
  sourceColor,
}: {
  minimal: boolean
  sourceColor: string
}) {
  return (
    <span
      aria-hidden="true"
      className="border-border relative size-5 shrink-0 overflow-hidden rounded-full border"
      style={minimal ? undefined : { backgroundColor: sourceColor }}
    >
      {minimal ? (
        <>
          <span
            className="absolute inset-y-0 left-0 w-1/2"
            style={{ backgroundColor: sourceColor }}
          />
          <span className="bg-on-surface absolute inset-y-0 right-0 w-1/2" />
        </>
      ) : null}
    </span>
  )
}
PaletteSwatch.displayName = 'PaletteSwatchIcon'

// Keeps a node if it (or any descendant) matched, rebuilding `children` to
// only the surviving branches. Preserves the real tree shape so search
// results render through the normal NavItem/collapsible UI, just filtered.
function pruneNavTree(
  nodes: StorybookNavNode[],
  matchedIds: Set<string>,
): StorybookNavNode[] {
  const result: StorybookNavNode[] = []
  for (const node of nodes) {
    const children = node.children
      ? pruneNavTree(node.children, matchedIds)
      : undefined
    if (matchedIds.has(node.id) || (children && children.length > 0)) {
      result.push(children ? { ...node, children } : node)
    }
  }
  return result
}

// Depth-first, matching render (visual) order.
function flattenStoryLeaves(nodes: StorybookNavNode[]): StorybookNavNode[] {
  const result: StorybookNavNode[] = []
  for (const node of nodes) {
    if (node.children?.length) result.push(...flattenStoryLeaves(node.children))
    else result.push(node)
  }
  return result
}

interface SidebarSearchProps {
  navTree: NavTree
  active: boolean
  pendingId?: string
  inputRef: React.RefObject<HTMLInputElement | null>
  onNavigate: (id: string) => void
  onNodeClick: (id: string) => void
  onClose: () => void
  children: React.ReactNode
}

function SidebarSearch({
  navTree,
  active,
  pendingId,
  inputRef,
  onNavigate,
  onNodeClick,
  onClose,
  children,
}: SidebarSearchProps) {
  const [search, setSearch] = useState('')
  const deferredSearch = useDeferredValue(search)
  const [activeIndex, setActiveIndex] = useState(0)
  const hasQuery = deferredSearch.trim().length > 0
  const resultsRef = useRef<HTMLDivElement>(null)

  // Every close path (blur, Escape, selection) routes through here so the
  // query never lingers into the next time search is opened.
  const close = () => {
    setSearch('')
    setActiveIndex(0)
    onClose()
  }

  // Only prune/expand the tree once there's an actual query — otherwise an
  // empty query would match everything and force the whole tree open just
  // from focusing the input. Filtering/pruning runs against the deferred
  // value so keystrokes stay responsive while the tree re-render lags behind.
  const matchedItems = hasQuery
    ? filter(
        navTree,
        navTree.bfs().filter((item) => item.type !== 'section'),
        deferredSearch,
      )
    : []
  const matchedIds = new Set(matchedItems.map((item) => item.id))
  const prunedTree = hasQuery
    ? pruneNavTree(navTree.navigation, matchedIds)
    : []
  const leaves = hasQuery ? flattenStoryLeaves(prunedTree) : []
  const activeLeaf = leaves[activeIndex]

  // Marks each matched term in every visible tree label via the CSS Custom
  // Highlight API — shows *why* a node matched without wrapping its text in
  // extra DOM nodes. Query terms are "/" or "." separated (see filter()
  // below) and each can match a different ancestor's label, so every term
  // is searched against every label independently.
  useEffect(() => {
    if ('highlights' in CSS && hasQuery && resultsRef.current) {
      const terms = deferredSearch
        .trim()
        .toLowerCase()
        .split(PATH_SEPARATOR)
        .map((term) => term.trim())
        .filter(Boolean)
      const ranges: Range[] = []
      const labels = resultsRef.current.querySelectorAll('[data-search-label]')
      for (const label of labels) {
        const textNode = label.firstChild
        if (!(textNode instanceof Text)) continue
        const text = textNode.data.toLowerCase()
        for (const term of terms) {
          const start = text.indexOf(term)
          if (start === -1) continue
          const range = new Range()
          range.setStart(textNode, start)
          range.setEnd(textNode, start + term.length)
          ranges.push(range)
        }
      }
      CSS.highlights?.set('storybook-search-match', new Highlight(...ranges))
    }
    return () => {
      CSS.highlights?.delete('storybook-search-match')
    }
  }, [deferredSearch, hasQuery])

  const selectLeaf = (item: StorybookNavNode) => {
    onNavigate(navTree.getFirstStoryId(item.id))
    close()
  }

  const searchBar = (
    <div className="-mx-4 mt-2 mb-2 px-4">
      <SB.Input
        data-testid="storybook-search-input"
        inputRef={inputRef}
        minimal
        value={search}
        onChange={(e) => {
          setSearch(e.target.value)
          setActiveIndex(0)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActiveIndex((i) => Math.min(i + 1, leaves.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActiveIndex((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            if (activeLeaf) selectLeaf(activeLeaf)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            close()
          }
        }}
        onBlur={(event) => {
          const nextTarget = event.relatedTarget
          if (
            nextTarget instanceof Node &&
            resultsRef.current?.contains(nextTarget)
          )
            return
          close()
        }}
        placeholder="Search for a story or component"
        leading={<SB.icons.SearchCode />}
      />
    </div>
  )

  if (!active) return children

  if (!hasQuery) {
    return (
      <>
        {searchBar}
        {children}
      </>
    )
  }

  return (
    <>
      {searchBar}
      <SB.Sidebar.Content className="-mx-4 px-4">
        <div ref={resultsRef}>
          {leaves.length === 0 && (
            <p className="text-on-surface-secondary py-6 text-center text-sm">
              No results found.
            </p>
          )}
          {prunedTree.map((item) => (
            <NavItem
              key={item.id}
              item={item}
              forceOpen
              highlightedId={activeLeaf?.id}
              pendingId={pendingId}
              onHighlight={(id) => {
                const index = leaves.findIndex((leaf) => leaf.id === id)
                if (index >= 0) setActiveIndex(index)
              }}
              onStoryClick={(id) => {
                // click bypasses the input's onBlur-closes-search behavior
                onNavigate(id)
                close()
              }}
              onNodeClick={onNodeClick}
            />
          ))}
        </div>
      </SB.Sidebar.Content>
    </>
  )
}

// "/" and "." both act as path separators, letting queries like
// "input/states" or "input.states" match a node nested under a parent.
const PATH_SEPARATOR = /[/.]/u

function getPathNames(navTree: NavTree, node: StorybookNavNode): string[] {
  const names = [node.name]
  let parent = navTree.getNodeParent(node.id)
  while (parent) {
    names.unshift(parent.name)
    parent = navTree.getNodeParent(parent.id)
  }
  return names
}

function scoreTerm(name: string, term: string): number {
  // Compare space-insensitively so path segments typed without spaces
  // (eg "iconbutton") still match display names like "Icon Button".
  const normalizedName = name.replaceAll(' ', '')
  const normalizedTerm = term.replaceAll(' ', '')
  if (normalizedName === normalizedTerm) return 100 // exact
  if (normalizedName.startsWith(normalizedTerm)) return 75 // prefix
  if (normalizedName.includes(normalizedTerm)) return 50 // substring
  return 0
}

export function filter(
  navTree: NavTree,
  flatNavigation: StorybookNavNode[],
  search: string,
): StorybookNavNode[] {
  const terms = search
    .trim()
    .toLowerCase()
    .split(PATH_SEPARATOR)
    .map((term) => term.trim())
    .filter(Boolean)
  if (terms.length === 0) return flatNavigation

  // 1) Compute a base score for each node, matching each "/" or "."
  // separated term in order against the node's ancestor path.
  const scored = flatNavigation.map((node) => {
    const path = getPathNames(navTree, node).map((name) => name.toLowerCase())

    let pathIndex = 0
    let score = 0
    for (const term of terms) {
      let termScore = 0
      for (const pathName of path.slice(pathIndex)) {
        pathIndex++
        termScore = scoreTerm(pathName, term)
        if (termScore > 0) break
      }
      if (termScore === 0) return { node, score: 0 }
      score += termScore
    }

    return { node, score }
  })

  // 2) Filter and sort:
  //   - Highest score first
  //   - If scores tie, shorter name first
  return scored
    .filter(({ score }) => score > 0)
    .toSorted((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      // tie → shortest name wins
      return a.node.name.length - b.node.name.length
    })
    .map((x) => x.node)
}

interface NavItemProps extends Base {
  item: StorybookNavNode
}

function NavItem({ item, ...props }: NavItemProps) {
  // If it's a section, render it as a group
  if (item.type === 'section') {
    return (
      <SB.Sidebar.Group className="py-0">
        <SB.Sidebar.GroupLabel className="h-auto justify-center pt-2 font-bold tracking-widest uppercase select-none">
          {item.name}
        </SB.Sidebar.GroupLabel>
        <SB.Sidebar.GroupContent>
          <SB.Sidebar.Menu className="gap-0">
            {(item.children ?? []).map((child) => (
              <SB.Sidebar.MenuItem key={child.id}>
                <NavItemContent item={child} {...props} />
              </SB.Sidebar.MenuItem>
            ))}
          </SB.Sidebar.Menu>
        </SB.Sidebar.GroupContent>
      </SB.Sidebar.Group>
    )
  }

  // For top-level non-section items, wrap them in a menu
  return (
    <SB.Sidebar.Menu>
      <SB.Sidebar.MenuItem>
        <NavItemContent item={item} {...props} />
      </SB.Sidebar.MenuItem>
    </SB.Sidebar.Menu>
  )
}

function NavItemContent({ item, ...props }: NavItemProps) {
  // Leaf node (no children)
  if (!item.children?.length) {
    const highlighted = props.highlightedId !== undefined
    const pending = item.id === props.pendingId
    return (
      <SB.Sidebar.TreeLeaf
        isActive={
          highlighted
            ? item.id === props.highlightedId
            : item.id === props.activeId
        }
        onClick={() => {
          props.onStoryClick(item.id)
        }}
        onMouseEnter={() => {
          props.onHighlight?.(item.id)
        }}
      >
        {pending ? (
          <span
            className="inline-flex size-4 shrink-0 items-center justify-center"
            role="status"
            aria-live="polite"
          >
            <LoadingIndicator
              className="size-4"
              aria-hidden="true"
            />
            <span className="sr-only">Loading {item.name}…</span>
          </span>
        ) : (
          getIcon(item.type)
        )}
        <span data-search-label>{item.name}</span>
      </SB.Sidebar.TreeLeaf>
    )
  }

  // Node with children
  return (
    <SB.Sidebar.TreeBranch
      forceOpen={props.forceOpen}
      revealKey={props.activeId?.includes(item.id) ? props.activeId : undefined}
      triggerProps={{ className: 'group/storybook-node pointer-fine:[&>svg:last-child]:hidden' }}
      trigger={
        <>
          <span className="relative flex size-4 shrink-0 items-center justify-center">
            <span className="pointer-fine:group-hover/storybook-node:invisible pointer-fine:group-focus-visible/storybook-node:invisible [&>svg]:size-4">
              {getIcon(item.type)}
            </span>
            <SB.icons.ChevronRight className="absolute inset-0 size-4 rotate-0 opacity-0 transition-[rotate,opacity] duration-200 pointer-fine:group-hover/storybook-node:opacity-100 pointer-fine:group-focus-visible/storybook-node:opacity-100 group-aria-expanded/storybook-node:rotate-90" />
          </span>
          <span data-search-label>{item.name}</span>
        </>
      }
    >
      {item.children.map((child) => (
        <SB.Sidebar.MenuSubItem key={child.id}>
          <NavItemContent item={child} {...props} />
        </SB.Sidebar.MenuSubItem>
      ))}
    </SB.Sidebar.TreeBranch>
  )
}

function getIcon(type: StorybookNavNode['type']) {
  switch (type) {
    case 'section':
      return <SB.icons.Layout />
    case 'page':
      return <SB.icons.Layers />
    case 'component':
      return <SB.icons.Puzzle />
    case 'story':
      return <SB.icons.FileText />
    default:
      return <SB.icons.FileText />
  }
}

interface Explorer {
  name: string
  logo: React.ElementType
  version: string
}

interface ExplorerSwitcherProps {
  activeExplorerName: string
  setActiveExplorerName: (v: string) => void
  explorers: Explorer[]
  settings: StorybookSidebarProps['settings']
  defaultControlledEndpoint?: string
  openReactQueryDevtools?: () => void
  openRouterDevtools?: () => void
  openTelemetryDevtools?: () => void
}

function ExplorerSwitcher({
  settings,
  activeExplorerName,
  setActiveExplorerName,
  explorers,
  defaultControlledEndpoint,
  openReactQueryDevtools,
  openRouterDevtools,
  openTelemetryDevtools,
}: ExplorerSwitcherProps) {
  const activeExplorer = explorers.find((e) => e.name === activeExplorerName)
  if (!activeExplorer) {
    throw new Error('No active team')
  }

  const handleOpenReactQueryDevtools = () => {
    openReactQueryDevtools?.()
  }

  const handleOpenRouterDevtools = () => {
    openRouterDevtools?.()
  }

  const handleOpenTelemetryDevtools = () => {
    openTelemetryDevtools?.()
  }

  return (
    <SB.Sidebar.Menu>
      <SB.Sidebar.MenuItem>
        <SB.DropdownMenu.Root>
          <SB.DropdownMenu.Trigger
            render={(props) => (
              <SB.Sidebar.MenuButton
                {...props}
                size="lg"
                className="data-[popup-open]:bg-surface-3 data-[popup-open]:text-on-surface"
              >
                <div className="bg-primary text-on-primary flex aspect-square size-8 items-center justify-center rounded-lg">
                  <activeExplorer.logo className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {activeExplorer.name}
                  </span>
                  <span className="truncate text-xs">
                    {activeExplorer.version}
                  </span>
                </div>
                <SB.icons.ChevronsUpDown className="ml-auto" />
              </SB.Sidebar.MenuButton>
            )}
          />
          <SB.DropdownMenu.Content
            className="w-[--anchor-width] min-w-56 rounded-lg"
            side="bottom"
            sideOffset={4}
          >
            <SB.DropdownMenu.Group>
              <SB.DropdownMenu.Label className="text-on-secondary text-xs">
                Explorers
              </SB.DropdownMenu.Label>
              {explorers.map((explorer, index) => (
                <SB.DropdownMenu.Item
                  key={explorer.name}
                  onClick={() => {
                    setActiveExplorerName(explorer.name)
                  }}
                  className="gap-2 p-2"
                >
                  <div className="flex size-6 items-center justify-center">
                    <explorer.logo className="size-5 shrink-0" />
                  </div>
                  {explorer.name}
                  <SB.DropdownMenu.Shortcut>
                    ⌘{index + 1}
                  </SB.DropdownMenu.Shortcut>
                </SB.DropdownMenu.Item>
              ))}
            </SB.DropdownMenu.Group>
            <SB.DropdownMenu.Separator />

            <SB.DropdownMenu.Item
              className="gap-2 p-2"
              onClick={() => {
                settings.setFakeNetwork((old) => {
                  const preciseControl = !old.preciseControl

                  return {
                    ...old,
                    type: preciseControl ? 'off' : 'fast',
                    preciseControl,
                    controlledEndpoints:
                      preciseControl && defaultControlledEndpoint
                        ? [defaultControlledEndpoint]
                        : [],
                  }
                })
              }}
            >
              <div className="flex size-6 items-center justify-center">
                <SB.icons.Microscope className="size-4" />
              </div>
              <div className="text-on-secondary mr-auto font-medium">
                {settings.fakeNetwork.preciseControl ? 'Generic' : 'Precise'}{' '}
                network control
              </div>
            </SB.DropdownMenu.Item>
            <SB.DropdownMenu.Sub>
              <SB.DropdownMenu.SubTrigger className="gap-2 p-2">
                <div className="flex size-6 items-center justify-center">
                  <SB.icons.Layout className="size-4" />
                </div>
                <div className="text-on-secondary mr-auto font-medium">
                  Add rule
                </div>
              </SB.DropdownMenu.SubTrigger>
              <SB.DropdownMenu.SubContent className="min-w-44">
                <SB.DropdownMenu.Item
                  onClick={() => {
                    settings.setRules((rules) =>
                      addStorybookRule(rules, 'vertical'),
                    )
                  }}
                >
                  <SB.icons.Minus className="rotate-90" />
                  Vertical rule
                </SB.DropdownMenu.Item>
                <SB.DropdownMenu.Item
                  onClick={() => {
                    settings.setRules((rules) =>
                      addStorybookRule(rules, 'horizontal'),
                    )
                  }}
                >
                  <SB.icons.Minus />
                  Horizontal rule
                </SB.DropdownMenu.Item>
              </SB.DropdownMenu.SubContent>
            </SB.DropdownMenu.Sub>
            {/* oxlint-disable typescript/prefer-nullish-coalescing -- intentional: truthy check for optional function props */}
            {(openReactQueryDevtools ||
              openRouterDevtools ||
              openTelemetryDevtools) && <SB.DropdownMenu.Separator />}
            {/* oxlint-enable typescript/prefer-nullish-coalescing */}
            {openReactQueryDevtools && (
              <SB.DropdownMenu.Item
                className="gap-2 p-2"
                onClick={handleOpenReactQueryDevtools}
              >
                <div className="bg-surface-1 flex size-6 items-center justify-center rounded-md border">
                  <SB.icons.TreePalm className="size-4" />
                </div>
                <div className="text-on-secondary mr-auto font-medium">
                  React Query tools
                </div>
              </SB.DropdownMenu.Item>
            )}
            {openRouterDevtools && (
              <SB.DropdownMenu.Item
                className="gap-2 p-2"
                onClick={handleOpenRouterDevtools}
              >
                <div className="bg-surface-1 flex size-6 items-center justify-center rounded-md border">
                  <SB.icons.Route className="size-4" />
                </div>
                <div className="text-on-secondary mr-auto font-medium">
                  Router tools
                </div>
              </SB.DropdownMenu.Item>
            )}
            {openTelemetryDevtools && (
              <SB.DropdownMenu.Item
                className="gap-2 p-2"
                onClick={handleOpenTelemetryDevtools}
              >
                <div className="bg-surface-1 flex size-6 items-center justify-center rounded-md border">
                  <SB.icons.Activity className="size-4" />
                </div>
                <div className="text-on-secondary mr-auto font-medium">
                  Telemetry tools
                </div>
                <SB.DropdownMenu.Shortcut>⌥S</SB.DropdownMenu.Shortcut>
              </SB.DropdownMenu.Item>
            )}
          </SB.DropdownMenu.Content>
        </SB.DropdownMenu.Root>
      </SB.Sidebar.MenuItem>
    </SB.Sidebar.Menu>
  )
}

interface TokenInputProps {
  tokens: string[]
  setTokens: (cb: (tokens: string[]) => string[]) => void
}

export function TokenInput({ setTokens, tokens }: TokenInputProps) {
  const [inputValue, setInputValue] = useState('')

  const addToken = () => {
    const trimmed = inputValue.trim()
    if (trimmed && !tokens.includes(trimmed)) {
      setTokens((current) => [...current, trimmed])
      setInputValue('')
    }
  }

  return (
    <Combobox
      multiple
      value={tokens}
      onValueChange={(newTokens) => {
        setTokens(() => newTokens)
      }}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      items={[]}
    >
      <ComboboxChips>
        {tokens.map((token) => (
          <ComboboxChip key={token}>{token}</ComboboxChip>
        ))}
        <ComboboxChipsInput
          placeholder={tokens.length === 0 ? 'Type and press Enter...' : ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              addToken()
            }
          }}
          onBlur={addToken}
        />
      </ComboboxChips>
    </Combobox>
  )
}
